import React, { useEffect } from "react";
import OrderSummary from "./OrderSummary";
import BillSummary from "./BillSummary";
import { Customer, OrderDetails, Product } from "../types/restaurant";
import OrderTypeToggle from "./OrderTypeToggle";
import { usePaymentTheme } from "../../respayment/hooks/usePaymentTheme";

interface OrderSectionProps {
  isOrderModified?: boolean;
  isDelivery: boolean;
  setIsDelivery: (value: boolean) => void;
  tableNumber: string;
  guess: string;
  setGuess: (v: string) => void;
  setTableNumber: (v: string) => void;
  oldProducts: OrderDetails | undefined;
  selectedProducts: Product[];
  editingNote: string | null;
  handleNoteChange: (orderId: string, note: string) => void;
  handleNoteClick: (productId: string) => void;
  handleNoteBlur: (orderId: string) => void;
  deleteProduct: (id: string) => void;
  handleDecrement: (item: Product) => void;
  handleIncrement: (item: Product) => void;
  calculateSubtotal: () => number;
  discount: number;
  setDiscount: (value: number) => void;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  calculateTotal: (subtotal: number) => number;
  handleCancelOrder: (adjust_type: string) => void;
  handleUpdatOrder: () => void;
  resetFormFields: () => void;
  setOpen: (value: boolean) => void;
  open: boolean;
  customers: Customer[];
  filteredCustomers: Customer[];
  setFilteredCustomers: (customers: Customer[]) => void;
  pagenumber: number;
  setPagenumber: (value: number) => void;
  total: { totalRecords: string; totalPage: string };
  sendorder: (customerId: string) => void;
  setCustomerAdd: (value: boolean) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  deliveryFee: number;
  setDeliveryFee: (value: number) => void;
  deliveryFeeEnabled: boolean;
}

const OrderSection: React.FC<OrderSectionProps> = (props) => {
  const subtotal = props.calculateSubtotal();
  const { themeBorderColor, themeTextColor, themeRingColor, getBgClass } = usePaymentTheme();

useEffect(() => {
  // If cart is empty → reset everything
  if (props.selectedProducts.length === 0) {
    props.setDiscount(0);
    props.setDeliveryFee(0);
    return;
  }

  // If discount exceeds subtotal → reset discount
  if (props.discount > subtotal) {
    props.setDiscount(0);
  }

  // If delivery fee exceeds subtotal → reset delivery fee
  if (props.deliveryFee > subtotal) {
    props.setDeliveryFee(0);
  }

}, [ props.selectedProducts, subtotal, props.discount, props.deliveryFee ]);


  return (
    <div className={`w-full lg:w-1/3 rounded-md ${themeBorderColor} shadow-md flex flex-col h-[90vh]`}>
      <div className={`${getBgClass()} px-2 py-1 rounded-t-2xl`}>
        <div className='flex items-center justify-between mb-1'>
          {props.oldProducts?.Orderid && (
            <span className='px-2 py-1 bg-white bg-opacity-20 text-white text-sm font-medium rounded-full backdrop-blur-sm'>
              Editing #{props.oldProducts.Orderid}
            </span>
          )}
        </div>

        <OrderTypeToggle isDelivery={props.isDelivery} setIsDelivery={props.setIsDelivery} orderId={props.oldProducts?.Orderid} />
      </div>

      {!props.isDelivery && (
        <div className="px-2 py-0.5 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              {
                label: "Dine In Guests:",
                value: props.guess,
                onChange: (v: string) => props.setGuess(v),
              },
              {
                label: "Table No:",
                value: props.tableNumber,
                onChange: (v: string) => props.setTableNumber(v),
              },
            ].map((field, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <label className={`text-xs sm:text-sm md:text-xs lg:text-sm xl:text-sm font-medium ${themeTextColor}`}>{field.label}</label> 
                <input
                  type="number"
                  className={`lg:w-[75px] xl:w-[85px] lg:h-7 xl:h-9 border ${themeBorderColor} rounded-lg px-3 text-sm font-medium focus:ring-2 ${themeRingColor}`}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  min="1"
                  max="20"
                  placeholder="0"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-hidden flex flex-col">
        <OrderSummary
          selectedProducts={props.selectedProducts}
          editingNote={props.editingNote}
          handleNoteChange={props.handleNoteChange}
          handleNoteClick={props.handleNoteClick}
          handleNoteBlur={props.handleNoteBlur}
          handleIncrement={props.handleIncrement}
          handleDecrement={props.handleDecrement}
          deleteProduct={props.deleteProduct}
        />
      </div>

      <div className={`border-t ${themeBorderColor} border-opacity-30 bg-white`}>
        <BillSummary
          subtotal={subtotal}
          discount={props.discount}
          setDiscount={props.setDiscount}
          calculateTotal={props.calculateTotal}
          handleCancelOrder={props.handleCancelOrder}
          handleUpdatOrder={props.handleUpdatOrder}
          open={props.open}
          setOpen={props.setOpen}
          customers={props.customers}
          filteredCustomers={props.filteredCustomers}
          setFilteredCustomers={props.setFilteredCustomers}
          sendorder={props.sendorder}
          pagenumber={props.pagenumber}
          setPagenumber={props.setPagenumber}
          total={props.total}
          setCustomerAdd={props.setCustomerAdd}
          setIsEditing={props.setIsEditing}
          isOrderModified={props.isOrderModified}
          selectedProducts={props.selectedProducts}
          isEditing={props.isEditing}
          searchTerm={props.searchTerm}
          setSearchTerm={props.setSearchTerm}
          resetFormFields={props.resetFormFields}
          deliveryFee={props.deliveryFee}
          setDeliveryFee={props.setDeliveryFee}
          deliveryFeeEnabled={props.isDelivery ? props.deliveryFeeEnabled : false} // Only enable delivery fee in Delivery mode
          isDelivery={props.isDelivery}
        />
      </div>
    </div>
  );
};

export default OrderSection;
